package co.maytes.standupbot.dashboard.features.home

import co.maytes.standupbot.dashboard.features.setup.SetupGlyph
import co.maytes.standupbot.dashboard.features.standups.ScheduleSpec
import co.maytes.standupbot.dashboard.features.standups.Weekday
import co.maytes.standupbot.dashboard.styles.HomeArt
import co.maytes.standupbot.shared.dto.integration.FixActionDto
import co.maytes.standupbot.shared.dto.setup.SetupItemDto
import co.maytes.standupbot.shared.dto.setup.SetupItemStateDto
import co.maytes.standupbot.shared.dto.setup.SetupStatusDto
import co.maytes.standupbot.shared.dto.standup.StandupDto
import co.maytes.standupbot.shared.dto.standuprun.StandupRunStatusDto
import co.maytes.standupbot.shared.dto.standuprun.StandupRunSummaryDto
import co.maytes.standupbot.shared.util.RelativeTime
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime

enum class NodeLink(val active: Boolean, val needsAction: Boolean, val actionLabel: String) {
    Unknown(true, false, ""),
    Connected(true, false, ""),
    Degraded(true, false, ""),
    Broken(false, true, "Reconnect"),
    Off(false, true, "Connect"),
}

enum class FlowNodeId(val label: String, val logo: String, val setupKey: String) {
    Linear("Linear", "/brand/linear.svg", "LINEAR"),
    Github("GitHub", "/brand/github.svg", "GITHUB"),
    Calendar("Calendar", "/brand/google.svg", "GOOGLE_CALENDAR"),
    Claude("Claude", "/brand/claude.svg", "ANTHROPIC"),
    Slack("Slack", "/brand/slack.svg", "SLACK"),
}

data class FlowNode(val id: FlowNodeId, val link: NodeLink, val actionPath: String?)

data class HomeFlow(val inputs: List<FlowNode>, val claude: FlowNode, val slack: FlowNode) {
    fun inputWireOn(node: FlowNode): Boolean = node.link.active
    val outputWireOn: Boolean get() = claude.link.active && slack.link.active

    companion object {
        private const val DEFAULT_FIX_PATH = "/app/settings/integrations"

        fun from(status: SetupStatusDto?, canAct: Boolean): HomeFlow {
            val byKey = status?.items.orEmpty().associateBy { it.key }
            fun node(id: FlowNodeId): FlowNode {
                val item = byKey[id.setupKey]
                val link = if (status == null) NodeLink.Unknown else linkOf(item)
                val path = if (canAct && link.needsAction) item?.fix?.path ?: DEFAULT_FIX_PATH else null
                return FlowNode(id, link, path)
            }
            return HomeFlow(
                inputs = listOf(FlowNodeId.Linear, FlowNodeId.Github, FlowNodeId.Calendar).map(::node),
                claude = node(FlowNodeId.Claude),
                slack = node(FlowNodeId.Slack),
            )
        }

        private fun linkOf(item: SetupItemDto?): NodeLink =
            when (item?.state) {
                SetupItemStateDto.DONE -> NodeLink.Connected
                SetupItemStateDto.WARNING -> NodeLink.Degraded
                SetupItemStateDto.BROKEN -> NodeLink.Broken
                SetupItemStateDto.TODO, SetupItemStateDto.OPTIONAL, null -> NodeLink.Off
            }
    }
}

enum class MilestoneId(val title: String, val keys: List<String>) {
    Connect("Connect your tools", listOf("SLACK", "LINEAR", "ANTHROPIC")),
    Team("Choose channel and people", listOf("CHANNEL", "ROSTER", "LINKS")),
    Context("Add more context", listOf("GITHUB", "GOOGLE_CALENDAR")),
    Launch("Post your first standup", listOf("FIRST_POST")),
}

data class JourneyStep(
    val key: String,
    val label: String,
    val detail: String?,
    val glyph: SetupGlyph,
    val action: FixActionDto?,
)

data class Milestone(val id: MilestoneId, val steps: List<JourneyStep>) {
    val doneCount: Int get() = steps.count { it.glyph == SetupGlyph.Done }
    val complete: Boolean get() = steps.all { it.glyph == SetupGlyph.Done }
    val countLabel: String get() = "$doneCount/${steps.size}"
}

data class SetupJourney(
    val milestones: List<Milestone>,
    val next: JourneyStep?,
    val percent: Int,
    val doneCount: Int,
    val totalCount: Int,
    val complete: Boolean,
    val pendingCount: Int,
) {
    val progressLabel: String get() = "$doneCount of $totalCount done"

    fun isNextMilestone(milestone: Milestone): Boolean = next != null && milestone.steps.any { it.key == next.key }

    companion object {
        fun from(status: SetupStatusDto): SetupJourney {
            val steps = status.items.map(::step)
            val known = MilestoneId.entries.flatMap { it.keys }.toSet()
            val milestones = MilestoneId.entries.mapNotNull { id ->
                val own = steps.filter { it.key in id.keys }
                val extra = if (id == MilestoneId.Context) steps.filter { it.key !in known } else emptyList()
                (own + extra).takeIf { it.isNotEmpty() }?.let { Milestone(id, it) }
            }
            val next = milestones.flatMap { it.steps }
                .firstOrNull { it.glyph == SetupGlyph.Broken }
                ?: milestones.flatMap { it.steps }.firstOrNull { it.glyph == SetupGlyph.Todo }
            return SetupJourney(
                milestones = milestones,
                next = next,
                percent = status.percentComplete,
                doneCount = steps.count { it.glyph == SetupGlyph.Done },
                totalCount = steps.size,
                complete = steps.isNotEmpty() && next == null,
                pendingCount = steps.count { it.glyph == SetupGlyph.Todo || it.glyph == SetupGlyph.Broken },
            )
        }

        private fun step(item: SetupItemDto): JourneyStep =
            JourneyStep(item.key, item.label, item.detail, glyph(item.state), item.fix)

        private fun glyph(state: SetupItemStateDto): SetupGlyph =
            when (state) {
                SetupItemStateDto.DONE -> SetupGlyph.Done
                SetupItemStateDto.WARNING -> SetupGlyph.Warning
                SetupItemStateDto.BROKEN -> SetupGlyph.Broken
                SetupItemStateDto.TODO -> SetupGlyph.Todo
                SetupItemStateDto.OPTIONAL -> SetupGlyph.Optional
            }
    }
}

data class HeroAction(val label: String, val path: String)

data class HeroLine(val text: String, val action: HeroAction?)

data class NextRunLabel(val whenLabel: String, val timeLabel: String, val relative: String)

data class DayBar(val date: LocalDate, val count: Int, val isToday: Boolean)

data class ActivityBars(val bars: List<DayBar>) {
    val total: Int get() = bars.sumOf { it.count }
    val peak: Int get() = bars.maxOfOrNull { it.count } ?: 0
    val isEmpty: Boolean get() = total == 0

    fun heightPercent(bar: DayBar): Int = if (peak == 0) 0 else bar.count * PERCENT / peak

    companion object {
        private const val PERCENT = 100

        fun from(
            runs: List<StandupRunSummaryDto>,
            now: Instant,
            tz: TimeZone,
            days: Int = HomeArt.BAR_DAYS,
        ): ActivityBars {
            val today = now.toLocalDateTime(tz).date
            val counts = runs
                .filter { it.status == StandupRunStatusDto.POSTED }
                .groupingBy { (it.postedAt ?: it.scheduledFor).toLocalDateTime(tz).date }
                .eachCount()
            val bars = (days - 1 downTo 0).map { offset ->
                val date = today.minus(offset, DateTimeUnit.DAY)
                DayBar(date, counts[date] ?: 0, offset == 0)
            }
            return ActivityBars(bars)
        }
    }
}

object HomeModel {
    private const val DAYS_AHEAD_NAMED = 6
    private const val PAD = 2
    private const val CENTS = 100

    fun greetingFor(hour: Int): String = when (hour) {
        in 0..4 -> "Up late"
        in 5..11 -> "Good morning"
        in 12..17 -> "Good afternoon"
        else -> "Good evening"
    }

    fun longDate(now: Instant, tz: TimeZone): String {
        val local = now.toLocalDateTime(tz)
        return "${titleCase(local.dayOfWeek.name)}, ${titleCase(local.month.name)} ${local.dayOfMonth}"
    }

    fun upcoming(standups: List<StandupDto>, now: Instant): List<StandupDto> =
        standups
            .filter { it.enabled && (it.nextRunAt ?: return@filter false) > now }
            .sortedBy { it.nextRunAt }

    fun monthStart(now: Instant, tz: TimeZone): Instant {
        val local = now.toLocalDateTime(tz)
        return LocalDateTime(local.year, local.monthNumber, 1, 0, 0).toInstant(tz)
    }

    fun postedThisMonth(runs: List<StandupRunSummaryDto>, now: Instant, tz: TimeZone): List<StandupRunSummaryDto> {
        val start = monthStart(now, tz)
        return runs.filter { it.scheduledFor >= start }
    }

    fun todayRun(runs: List<StandupRunSummaryDto>, now: Instant, tz: TimeZone): StandupRunSummaryDto? {
        val today = now.toLocalDateTime(tz).date
        return runs.firstOrNull { it.scheduledFor.toLocalDateTime(tz).date == today }
    }

    fun nextRunLabel(next: Instant, now: Instant, tz: TimeZone): NextRunLabel {
        val local = next.toLocalDateTime(tz)
        val daysUntil = local.date.toEpochDays() - now.toLocalDateTime(tz).date.toEpochDays()
        val whenLabel = when {
            daysUntil <= 0 -> "Today"
            daysUntil == 1 -> "Tomorrow"
            daysUntil <= DAYS_AHEAD_NAMED -> titleCase(local.dayOfWeek.name)
            else -> "${titleCase(local.month.name)} ${local.dayOfMonth}"
        }
        return NextRunLabel(whenLabel, clock(local), RelativeTime.formatFuture(next, now))
    }

    fun activeDays(cron: String): Set<Int> {
        val days = ScheduleSpec.parse(cron, "UTC").days
        return (if (days.isEmpty()) Weekday.entries.toSet() else days).map { it.ordinal }.toSet()
    }

    fun hero(
        canManage: Boolean,
        journey: SetupJourney?,
        standups: List<StandupDto>?,
        now: Instant,
        tz: TimeZone,
    ): HeroLine {
        if (journey != null && !journey.complete) return setupLine(journey)
        if (standups == null) return HeroLine("", null)
        val next = upcoming(standups, now).firstOrNull()
        return when {
            next != null -> {
                val standupTz = runCatching { TimeZone.of(next.timezone) }.getOrDefault(tz)
                val label = nextRunLabel(next.nextRunAt!!, now, standupTz)
                HeroLine(
                    "Next standup: ${label.whenLabel} at ${label.timeLabel}, ${label.relative}.",
                    HeroAction("View standups", "/app/standups"),
                )
            }
            standups.isEmpty() && canManage ->
                HeroLine("No standups yet. Create one to start posting.", HeroAction("New standup", "/app/standups/new"))
            standups.isEmpty() -> HeroLine("No standups yet. Ask an admin to create one.", null)
            else -> HeroLine("Nothing scheduled. Check your standup schedules.", HeroAction("View standups", "/app/standups"))
        }
    }

    fun formatDollars(cents: Int): String = "\$${cents / CENTS}.${(cents % CENTS).toString().padStart(PAD, '0')}"

    private fun setupLine(journey: SetupJourney): HeroLine {
        val next = journey.next
        val slackMissing = journey.milestones.flatMap { it.steps }.any { it.key == "SLACK" && it.glyph != SetupGlyph.Done }
        return when {
            slackMissing -> HeroLine("Connect Slack to get started.", null)
            next != null -> HeroLine("${stepsLeft(journey.pendingCount)}. Next: ${next.label}.", null)
            else -> HeroLine("Finish setup to go live.", null)
        }
    }

    private fun stepsLeft(count: Int): String = if (count == 1) "1 setup step left" else "$count setup steps left"

    private fun clock(local: LocalDateTime): String =
        "${local.hour.toString().padStart(PAD, '0')}:${local.minute.toString().padStart(PAD, '0')}"

    private fun titleCase(raw: String): String = raw.lowercase().replaceFirstChar { it.uppercase() }
}
